import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import type { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles'
import React, { memo, useMemo } from 'react'
import type { ContentEditorProps } from '../ContentEditor'
import { extractSuggestions } from '../ContentEditor/contentUtils'
import { withLazyLoad } from '../withLazyComponent'

/** Props for ReplacementOption component */
interface ReplacementOptionProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** The class object to apply the styling */
    classes?: any
    /** Flag that indicates whether the ReplacementOptions are inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** Optional key for the component */
    key?: string
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/**
 * A component that renders ReplacementOption content.
 * It uses Typography from Material-UI with optional styling to render a proper ReplacementOption content.
 * @param {ReplacementOptionProps} ReplacementOptionProps - Props for the component.
 */
export const ReplacementOption = memo<ReplacementOptionProps>(
    ({ contentItem, classes, inactive = false, editable = false, key }) => {
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            notDisabledBoxedInputContainer,
        } = useOptionGroupStyles()

        const contentItemId = contentItem.id || ''
        const contentItemText = contentItem.text?.trim() || ''
        const testId = `replacement-option-${contentItem.id || 'default'}`

        const initialData = useMemo(() => `<p>${contentItemText}</p>`, [
            contentItemText,
        ])
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem.id, contentItem.changeType]
        )
        const { active, placeholderRef, activate } = useCKEditorToggle(
            contentItemId
        )

        const isDisabled =
            inactive || contentItem.optionItemState === 'InActive'

        return (
            <Typography
                key={key || testId}
                data-testid={testId}
                data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                tabIndex={0}
                ref={active ? placeholderRef : null}
                onFocusCapture={() => activate()}
                onClick={() => activate()}
                className={clsx(
                    classes,
                    editableText,
                    editableTextBoxed,
                    isDisabled
                        ? disabledBoxedInputContainer
                        : notDisabledBoxedInputContainer
                )}
            >
                {active && !isDisabled && editable ? (
                    <ContentEditor
                        initialData={initialData}
                        initialSuggestions={initialSuggestions}
                    />
                ) : (
                    contentItemText
                )}
            </Typography>
        )
    }
)
